import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, Scroll } from '@angular/router';
import { TranslateService } from '@app/shared/services/translate.service';

/**
 * Service to manage errors displayed by ErrorComponent
 * Note: Fully reactive using Angular 20+ signals
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorService {
  // Services
  private router = inject(Router);
  private translateService = inject(TranslateService);

  // Signals
  private _errors = signal<string[]>([]);

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      // Scroll comes after the new page opened: keep the errors it already shows
      if (event instanceof Scroll) {
        this.showNavigationStateErrors();
      } else {
        // Clear errors when user changes page
        this.clear();
      }
    });
  }

  private showNavigationStateErrors(): void {
    const { errors, ...scrubbed } = history.state ?? {};
    if (errors?.length > 0) {
      this.errors = errors;
      history.replaceState(scrubbed, '', location.href);
    }
  }

  clear(): void {
    this._errors.set([]);
  }

  get errors(): string[] {
    return this._errors();
  }

  set errors(errors: string[]) {
    this.setErrorsI18N(errors);
  }

  private setErrorsI18N(errors: string[]): void {
    if (errors == null || errors.length === 0) return;

    if (Array.isArray(errors)) {
      const translatedErrors = errors.map((error) => this.translateService.instantWithLegacyParameters(error, false));
      this._errors.set(translatedErrors);

      // Scroll to top
      window.scrollTo(0, 0);
    }
  }
}
